import * as L from 'leaflet'

export function geojsonToWKT(geometry: any): string {
  if (!geometry || geometry.type !== 'Polygon') return ''
  const coordinates = geometry.coordinates[0]
  const wktCoordinates = coordinates
    .map(([lng, lat]: [number, number]) => `${lng} ${lat}`)
    .join(', ')
  return `SRID=4326;POLYGON ((${wktCoordinates}))`
}

export function downloadFile(url: string, filename: string) {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
}

export function displayGeometryOnMap(geometry: string, drawnItems: any, map: any) {
  if (!geometry) return
  try {
    const matches = geometry.match(/POLYGON\s*\(\((.+?)\)\)/)
    if (!matches || matches.length < 2) {
      console.error('Invalid geometry format')
      return
    }
    const coordinates = matches[1].split(',').map((coord) => coord.trim().split(' ').map(Number))
    const latLngs: [number, number][] = coordinates.map(
      ([lng, lat]) => [lat, lng] as [number, number]
    )
    const polygon = L.polygon(latLngs, { color: 'blue', weight: 2 })
    drawnItems.addLayer(polygon)
    map.fitBounds(polygon.getBounds())
  } catch (error) {
    console.error('Error parsing geometry:', error)
  }
}

export function startPulling(
  generateInputStore: any,
  currentItem: any,
  loadingMessage: any,
  isLoading: any,
  pullingInterval: any
) {
  pullingInterval.value = setInterval(async () => {
    try {
      const scenarioId = currentItem.value.id
      await generateInputStore.fetchGenerateInputs(scenarioId)

      const currentGenerateInput = generateInputStore.currentItem
      if (currentGenerateInput?.status === 'Success') {
        loadingMessage.value = 'Generate Data Successfully!'
        isLoading.value = true
        clearInterval(pullingInterval.value)
        pullingInterval.value = null
      } else if (currentGenerateInput?.status === 'Failed') {
        loadingMessage.value = 'The process failed. Please try again.'
        isLoading.value = true
        clearInterval(pullingInterval.value)
        pullingInterval.value = null
      }
    } catch (error) {
      console.error('Error during pulling:', error)
    }
  }, 1000)
}

export function stopPulling(pullingInterval: any, isLoading: any) {
  if (pullingInterval.value) {
    clearInterval(pullingInterval.value)
    pullingInterval.value = null
  }
  isLoading.value = false
}

export function handleShapeCreated(e: any, drawnItems: any, showWarningBox: (msg: string) => void) {
  if (drawnItems.getLayers().length > 0) {
    showWarningBox('Only one shape is allowed. Please delete the existing shape to draw a new one.')
    return
  }
  drawnItems.addLayer(e.layer)
}

export function handleDrawStart(
  e: any,
  drawnItems: any,
  map: any,
  showWarningBox: (msg: string) => void
) {
  if (drawnItems.getLayers().length > 0) {
    map.fire('draw:drawstop', { layerType: e.layerType })
    showWarningBox('Only one shape is allowed. Please delete the existing shape to draw a new one.')
  }
}

export function handleDownload(
  event: Event,
  generateInputStore: any,
  currentItem: any,
  drawnItems: any,
  isLoading: any,
  loadingMessage: any,
  pullingInterval: any,
  getFileDisabled: any,
  reRunDisabled: any,
  showWarningBox: (msg: string) => void
) {
  event.preventDefault()

  const scenarioId = currentItem.value.id

  getFileDisabled.value = true

  generateInputStore.fetchGenerateInputs(scenarioId).then(async () => {
    const currentGenerateInput = generateInputStore.currentItem

    if (currentGenerateInput?.status === 'Running') {
      isLoading.value = true
      loadingMessage.value = 'Processing, please wait...'
      startPulling(generateInputStore, currentItem, loadingMessage, isLoading, pullingInterval)
      return
    }

    if (drawnItems && drawnItems.getLayers().length > 0) {
      const geojson = drawnItems.toGeoJSON()
      const currentShapeGeometry = geojson.features[0]?.geometry
      const currentShapeWKT = geojsonToWKT(currentShapeGeometry)
      const requestBody = {
        simulation: currentItem.value.id,
        geometry: currentShapeGeometry
      }

      if (currentGenerateInput?.status === 'Success') {
        if (currentGenerateInput.geometry === currentShapeWKT) {
          getFileDisabled.value = false
          isLoading.value = true
          loadingMessage.value = 'Generate Data Successfully!'
          return
        }
      } else if (currentGenerateInput?.status === 'Failed') {
        reRunDisabled.value = false
        isLoading.value = true
        loadingMessage.value = 'The process failed. Please try again.'
        return
      } else if (currentGenerateInput?.status === 'Pending') {
        isLoading.value = true
        loadingMessage.value =
          'This task is currently pending in a calculation queue. Please be patient.'
        return
      }

      try {
        if (currentGenerateInput && currentGenerateInput.id) {
          await generateInputStore.updateGenerateInputGeometry(
            currentGenerateInput.id!,
            requestBody.simulation,
            requestBody.geometry
          )
          isLoading.value = true
          loadingMessage.value = 'Processing, please wait...'
          startPulling(generateInputStore, currentItem, loadingMessage, isLoading, pullingInterval)
        } else {
          await generateInputStore.createGenerateInput(requestBody.simulation, requestBody.geometry)
          isLoading.value = true
          loadingMessage.value = 'Processing, please wait...'
          startPulling(generateInputStore, currentItem, loadingMessage, isLoading, pullingInterval)
        }
      } catch (error: any) {
        console.error('Error handling generate input:', error)
        let message = 'An error occurred. Please try again.'
        if (error.statusText && error.statusText.error) {
          message = error.statusText.error
        } else if (error.response && error.response.data && error.response.data.error) {
          message = error.response.data.error
        } else if (error.response && typeof error.response.data === 'string') {
          message = error.response.data
        } else if (error.message) {
          message = error.message
        }
        showWarningBox(message)
      }
    } else {
      showWarningBox('Please draw a shape first.')
    }
  })
}

export function handleReRun(
  currentItem: any,
  drawnItems: any,
  isLoading: any,
  loadingMessage: any,
  reRunDisabled: any,
  showWarningBox: (msg: string) => void,
  startPulling: (generateInputStore: any) => void,
  useGenerateInputStore: () => any
) {
  const generateInputStore = useGenerateInputStore()
  const simulationId = currentItem.value.id

  reRunDisabled.value = true

  if (drawnItems && drawnItems.getLayers().length > 0) {
    const geojson = drawnItems.toGeoJSON()
    const currentShapeGeometry = geojson.features[0]?.geometry
    const requestBody = {
      simulation: simulationId,
      geometry: currentShapeGeometry
    }

    try {
      const currentGenerateInput = generateInputStore.currentItem
      if (currentGenerateInput && currentGenerateInput.id) {
        generateInputStore.updateGenerateInputGeometry(
          currentGenerateInput.id,
          requestBody.simulation,
          requestBody.geometry
        )
        isLoading.value = true
        loadingMessage.value = 'Processing, please wait...'
        startPulling(generateInputStore)
      }
    } catch (error: any) {
      console.error('Error handling generate input:', error)
      let message = 'An error occurred. Please try again.'
      const err = error as { response?: any; statusText?: any; message?: string }
      if (err.statusText && err.statusText.error) {
        message = err.statusText.error
      } else if (err.response && err.response.data && err.response.data.error) {
        message = err.response.data.error
      } else if (err.response && typeof err.response.data === 'string') {
        message = err.response.data
      } else if (err.message) {
        message = err.message
      }
      showWarningBox(message)
    }
  } else {
    showWarningBox('Please draw a shape first.')
  }
}
